import type { CountryConfigsQuery } from '@/__generated__/gateway/graphql';
import { resolveResourceOnStore } from '@/shared/utils/deep-link-equivalence';
import { parseShopifyResourcePath } from '@/shared/utils/shopify-resource-path';
import { stripMarketPathPrefix } from '@/shared/utils/urls';

export type CountryConfig = NonNullable<
  NonNullable<CountryConfigsQuery['countryConfigs']>[number]
>;

// Several public domains (storeEndpoint) can share one Shopify store - the
// EUR store alone serves ten. Grouping by storefront.endpoint, the API
// endpoint actually queried, is what keeps the request count to one per
// store rather than one per domain.
const groupConfigsByStorefrontEndpoint = (
  countryConfigs: CountryConfig[]
): Map<string, CountryConfig[]> => {
  const configsByStorefrontEndpoint = new Map<string, CountryConfig[]>();

  countryConfigs.forEach((config) => {
    const storefrontEndpoint = config.shopifyConfig?.storefront?.endpoint;

    if (!storefrontEndpoint) {
      return;
    }

    const group = configsByStorefrontEndpoint.get(storefrontEndpoint) ?? [];
    group.push(config);
    configsByStorefrontEndpoint.set(storefrontEndpoint, group);
  });

  return configsByStorefrontEndpoint;
};

const getHostnameSafely = (url: string): string | null => {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

const preserveAllStores = (
  countryConfigs: CountryConfig[]
): Map<string, boolean> => {
  const result = new Map<string, boolean>();

  countryConfigs.forEach((config) => {
    const storeEndpoint = config.shopifyConfig?.storeEndpoint;

    if (storeEndpoint) {
      result.set(storeEndpoint, true);
    }
  });

  return result;
};

// Resolves whether the current path should be preserved (vs falling back to
// the homepage) for each unique store among the given country configs.
export const resolvePreservePathByStore = async (
  countryConfigs: CountryConfig[]
): Promise<Map<string, boolean>> => {
  const pathname = stripMarketPathPrefix(window.location.pathname);

  if (pathname === '/') {
    return preserveAllStores(countryConfigs);
  }

  const resource = parseShopifyResourcePath(pathname);

  // Unparseable paths (/search, /account, /blogs/...) keep the path
  // unchanged - no behaviour change from before this resolver existed.
  if (!resource) {
    return preserveAllStores(countryConfigs);
  }

  const currentConfig = countryConfigs.find((config) => {
    const storeEndpoint = config.shopifyConfig?.storeEndpoint;
    return (
      storeEndpoint != null &&
      getHostnameSafely(storeEndpoint) === window.location.hostname
    );
  });
  const currentStorefront = currentConfig?.shopifyConfig?.storefront;

  // The page rendering only proves the URL resolves via Shopify's own
  // routing (e.g. the built-in /collections/all listing) - it does not prove
  // a resource with this handle exists. Checking the store the visitor is
  // already on tells us whether our {type, handle} model describes this URL
  // at all: a built-in route returns 200 while collectionByHandle returns
  // null there too, so a 'missing' result here means every store should fail
  // open, not just this one. If we can't identify the current store, or
  // can't check it, we fail open the same way - we've learned nothing.
  if (
    !currentConfig ||
    !currentStorefront?.endpoint ||
    !currentStorefront?.publicAccessToken
  ) {
    return preserveAllStores(countryConfigs);
  }

  const currentStoreStatus = await resolveResourceOnStore(
    currentStorefront.endpoint,
    currentStorefront.publicAccessToken,
    resource
  );

  if (currentStoreStatus !== 'exists') {
    return preserveAllStores(countryConfigs);
  }

  const result = new Map<string, boolean>();
  const groupsByStorefrontEndpoint =
    groupConfigsByStorefrontEndpoint(countryConfigs);

  // A config with no storefront.endpoint can't be grouped or checked at all.
  countryConfigs.forEach((config) => {
    const storeEndpoint = config.shopifyConfig?.storeEndpoint;
    if (storeEndpoint && !config.shopifyConfig?.storefront?.endpoint) {
      result.set(storeEndpoint, true);
    }
  });

  // Every domain sharing the current storefront endpoint is the same store,
  // same catalog, same answer we just confirmed - no need to check it again.
  const currentGroup =
    groupsByStorefrontEndpoint.get(currentStorefront.endpoint) ?? [];
  currentGroup.forEach((config) => {
    const storeEndpoint = config.shopifyConfig?.storeEndpoint;
    if (storeEndpoint) {
      result.set(storeEndpoint, true);
    }
  });

  await Promise.all(
    Array.from(groupsByStorefrontEndpoint.entries())
      .filter(
        ([storefrontEndpoint]) =>
          storefrontEndpoint !== currentStorefront.endpoint
      )
      .map(async ([storefrontEndpoint, configs]) => {
        const publicAccessToken =
          configs[0].shopifyConfig?.storefront?.publicAccessToken;

        // Fail-open: an 'unknown' result (ad blocker, offline, unreachable
        // store) preserves the path exactly like 'exists' - only a confirmed
        // 'missing' falls back to the homepage. No token means the group
        // can't be checked at all, which fails open the same way.
        const preservePath = publicAccessToken
          ? (await resolveResourceOnStore(
              storefrontEndpoint,
              publicAccessToken,
              resource
            )) !== 'missing'
          : true;

        configs.forEach((config) => {
          const storeEndpoint = config.shopifyConfig?.storeEndpoint;
          if (storeEndpoint) {
            result.set(storeEndpoint, preservePath);
          }
        });
      })
  );

  return result;
};
